import { useEffect, useState } from 'react';
import api from '../utils/api';
import { Wrench, AlertCircle, Plus, Check, Trash2 } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import dayjs from 'dayjs';

const TARGET_LABEL = {
  nozzle: '노즐', belt: '벨트', bed: '베드',
  chamber_filter: '챔버 필터', ams_wheel: 'AMS 휠',
  lubrication: '윤활', custom: '기타',
};
const TRIGGER_LABEL = { hours: '시간', jobs: '잡 수', days: '일수' };

function RuleModal({ rule, printers, onClose, onSaved }) {
  const [form, setForm] = useState(rule || {
    name: '', target: 'nozzle', triggerType: 'hours',
    threshold: 300, printerId: '', isActive: true,
  });
  const save = async () => {
    if (!form.name || !form.threshold) return alert('이름, 임계값 필수');
    try {
      if (rule?.id) await api.put(`/maintenance/rules/${rule.id}`, form);
      else await api.post('/maintenance/rules', form);
      onSaved(); onClose();
    } catch (e) { alert(e.response?.data?.message || e.message); }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 max-w-md w-full">
        <h2 className="text-white font-semibold mb-4">{rule ? '정비 규칙 수정' : '새 정비 규칙'}</h2>
        <div className="space-y-3">
          <input
            placeholder="규칙 이름 (예: 노즐 교체)"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              value={form.target}
              onChange={e => setForm(f => ({ ...f, target: e.target.value }))}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              {Object.entries(TARGET_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select
              value={form.triggerType}
              onChange={e => setForm(f => ({ ...f, triggerType: e.target.value }))}
              className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              {Object.entries(TRIGGER_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">
              임계값 ({TRIGGER_LABEL[form.triggerType]})
            </label>
            <input
              type="number"
              value={form.threshold}
              onChange={e => setForm(f => ({ ...f, threshold: +e.target.value }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            />
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">대상 프린터 (미선택 시 전체)</label>
            <select
              value={form.printerId || ''}
              onChange={e => setForm(f => ({ ...f, printerId: e.target.value || null }))}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">전체 프린터</option>
              {printers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-white text-sm">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))}
            />
            활성화
          </label>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm">취소</button>
          <button onClick={save} className="flex-1 py-2 bg-brand text-white rounded-lg text-sm">저장</button>
        </div>
      </div>
    </div>
  );
}

export default function MaintenancePage() {
  const [rules, setRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [modal, setModal] = useState(null);

  const load = () => {
    Promise.all([
      api.get('/maintenance/rules'),
      api.get('/maintenance/alerts'),
      api.get('/maintenance/logs?limit=30'),
      api.get('/printers'),
    ]).then(([r, a, l, p]) => {
      setRules(r.data); setAlerts(a.data); setLogs(l.data); setPrinters(p.data);
    });
  };

  useEffect(() => { load(); }, []);

  const deleteRule = async (id) => {
    if (!confirm('정비 규칙을 삭제할까요?')) return;
    await api.delete(`/maintenance/rules/${id}`);
    load();
  };

  const recordLog = async (alert) => {
    const notes = prompt(`${alert.ruleName} 정비 수행 메모:`) || '';
    await api.post('/maintenance/logs', {
      ruleId: alert.ruleId,
      printerId: alert.printerId,
      target: alert.target,
      notes,
      resetNozzle: alert.target === 'nozzle',
    });
    load();
  };

  return (
    <div>
      <PageHeader icon={Wrench} title="예방 정비 (PM)">
        <button
          onClick={() => setModal({})}
          className="flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-lg text-sm"
        >
          <Plus size={14} /> 규칙 추가
        </button>
      </PageHeader>

      {/* 활성 알림 */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-4">
        <h2 className="text-white font-semibold text-sm mb-4">
          <AlertCircle className="inline mr-2 text-orange-400" size={16} />
          조치 필요 알림
          {alerts.length > 0 && (
            <span className="ml-2 bg-orange-500/20 text-orange-400 text-xs px-2 py-0.5 rounded-full">
              {alerts.length}
            </span>
          )}
        </h2>
        {alerts.length === 0 && <p className="text-gray-600 text-sm">조치가 필요한 정비가 없습니다</p>}
        <div className="space-y-2">
          {alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-3 bg-orange-500/10 border border-orange-500/30 rounded-lg p-3">
              <div className="flex-1">
                <p className="text-white text-sm font-semibold">{a.ruleName} - {a.printerName}</p>
                <p className="text-xs text-gray-300">
                  {a.current}{a.unit} / {a.threshold}{a.unit} · <span className="text-orange-300">+{a.overBy}{a.unit} 초과</span>
                </p>
                {a.lastPerformedAt && (
                  <p className="text-xs text-gray-500">
                    최근 수행: {dayjs(a.lastPerformedAt).format('YYYY-MM-DD')}
                  </p>
                )}
              </div>
              <button
                onClick={() => recordLog(a)}
                className="px-3 py-1.5 bg-green-600/30 hover:bg-green-600/50 text-green-300 rounded text-xs font-medium"
              >
                <Check size={12} className="inline mr-1" />
                수행 완료
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* 정비 규칙 */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-4">
        <h2 className="text-white font-semibold text-sm mb-4">정비 규칙</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              <th className="text-left pb-2">이름</th>
              <th className="text-left pb-2">대상</th>
              <th className="text-left pb-2">트리거</th>
              <th className="text-left pb-2">임계값</th>
              <th className="text-left pb-2">프린터</th>
              <th className="text-left pb-2">활성</th>
              <th className="text-left pb-2"></th>
            </tr>
          </thead>
          <tbody>
            {rules.map(r => (
              <tr key={r.id} className="border-b border-gray-800/50">
                <td className="py-2 text-white">{r.name}</td>
                <td className="py-2 text-gray-400">{TARGET_LABEL[r.target]}</td>
                <td className="py-2 text-gray-400">{TRIGGER_LABEL[r.triggerType]}</td>
                <td className="py-2 text-gray-300">{r.threshold}</td>
                <td className="py-2 text-gray-400 text-xs">{r.printer?.name || '전체'}</td>
                <td className="py-2">
                  {r.isActive
                    ? <span className="text-green-400 text-xs">● 활성</span>
                    : <span className="text-gray-600 text-xs">● 비활성</span>}
                </td>
                <td className="py-2">
                  <button onClick={() => setModal(r)} className="text-blue-400 text-xs mr-2">수정</button>
                  <button onClick={() => deleteRule(r.id)} className="text-red-400"><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {rules.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-gray-600">규칙 없음</td></tr>}
          </tbody>
        </table>
      </div>

      {/* 수행 기록 */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h2 className="text-white font-semibold text-sm mb-4">수행 기록</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-gray-500 text-xs border-b border-gray-800">
              <th className="text-left pb-2">날짜</th>
              <th className="text-left pb-2">규칙</th>
              <th className="text-left pb-2">대상</th>
              <th className="text-left pb-2">프린터</th>
              <th className="text-left pb-2">메모</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id} className="border-b border-gray-800/50">
                <td className="py-2 text-gray-400 text-xs">{dayjs(l.performedAt).format('MM-DD HH:mm')}</td>
                <td className="py-2 text-white">{l.rule?.name || '-'}</td>
                <td className="py-2 text-gray-400">{TARGET_LABEL[l.target]}</td>
                <td className="py-2 text-gray-400">{l.printer?.name}</td>
                <td className="py-2 text-gray-500 text-xs">{l.notes || '-'}</td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-gray-600">기록 없음</td></tr>}
          </tbody>
        </table>
      </div>

      {modal !== null && (
        <RuleModal
          rule={modal?.id ? modal : null}
          printers={printers}
          onClose={() => setModal(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
